import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ResistanceIndicator from './ResistanceIndicator';
import { getMaterialMeta } from './materialMeta';
import { Box3DInteractive } from './Box3D';
import CustomerForm from './CustomerForm';
import { useAuth } from './AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Slider } from './ui/slider';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { 
  Package, Ruler, Layers, Printer, ShoppingCart, 
  ArrowRight, ArrowLeft, Upload, Check, AlertCircle, Loader2, Type, Bookmark
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, name: 'Dimensiones', icon: Ruler },
  { id: 2, name: 'Material', icon: Layers },
  { id: 3, name: 'Impresión', icon: Printer },
  { id: 4, name: 'Cantidad', icon: ShoppingCart },
];

// Fixed dimension limits: min 7cm (70mm), max width 250cm (2500mm)
const DIMENSION_LIMITS = {
  length: { min: 70, max: 99999 },
  width: { min: 70, max: 2500 },
  height: { min: 70, max: 99999 }
};

// Conversion helpers: UI shows cm, internal state is mm
const mmToCm = (mm) => {
  if (mm === '' || mm === null || mm === undefined) return '';
  const val = parseFloat(mm) / 10;
  return Number.isInteger(val) ? val : parseFloat(val.toFixed(1));
};
const cmToMm = (cm) => {
  if (cm === '' || cm === null || cm === undefined) return '';
  return Math.round(parseFloat(cm) * 10);
};

const Configurator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [dimensionError, setDimensionError] = useState(null);
  const [savingDesign, setSavingDesign] = useState(false);
  
  // Form state - start with empty dimensions for better UX
  const [config, setConfig] = useState({
    dimensions: { length: '', width: '', height: '' },
    material_id: null,
    printing: { has_printing: false, coverage_percent: 0, artwork_file_id: null },
    quantity: 1,
    billing: {
      billing_type: 'persona_fisica',
      name_or_company: '', rfc: '', email: '',
      country: 'México', state: '', street: '',
      neighborhood: '', city: '', postal_code: '', references: '',
      razon_social: '', invoice_email: '', cfdi_use: '', fiscal_same_as_shipping: false,
    },
    shipping: {
      same_as_billing: false, contact_name: '', company: '',
      country: 'México', state: '', street: '',
      neighborhood: '', city: '', postal_code: '',
      references: '', additional_instructions: '',
    },
    phone: { country_code: '+52', number: '' },
    invoice_requested: false,
  });
  const [formErrors, setFormErrors] = useState({});

  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState(null);
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [validatingArtwork, setValidatingArtwork] = useState(false);
  const [artworkValidation, setArtworkValidation] = useState(null);
  const [printSide, setPrintSide] = useState('front');
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Print mode: 'file' or 'text'
  const [printMode, setPrintMode] = useState('file');
  const DEFAULT_TEXT = { text_content: '', text_color: '#222222', font_family: 'Arial', font_size: 'medium', text_alignment: 'center' };
  const [textFaces, setTextFaces] = useState({
    front: { ...DEFAULT_TEXT },
    back: { ...DEFAULT_TEXT },
    left: { ...DEFAULT_TEXT },
    right: { ...DEFAULT_TEXT },
  });
  const [activeTextFace, setActiveTextFace] = useState('front');
  const textCustomization = textFaces[activeTextFace] || DEFAULT_TEXT;
  const TEXT_MAX_CHARS = 80;

  const FACE_TABS = [
    { value: 'front', label: 'Frente' },
    { value: 'back', label: 'Atrás' },
    { value: 'left', label: 'Izq.' },
    { value: 'right', label: 'Der.' },
  ];

  const FONT_OPTIONS = [
    { value: 'Arial', label: 'Arial' },
    { value: 'Helvetica', label: 'Helvetica' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Verdana', label: 'Verdana' },
    { value: 'Courier', label: 'Courier' },
    { value: 'Impact', label: 'Impact' },
  ];
  const SIZE_OPTIONS = [
    { value: 'small', label: 'Pequeño' },
    { value: 'medium', label: 'Mediano' },
    { value: 'large', label: 'Grande' },
  ];
  const ALIGN_OPTIONS = [
    { value: 'left', label: 'Izquierda' },
    { value: 'center', label: 'Centrado' },
    { value: 'right', label: 'Derecha' },
  ];

  const updateText = (key, value) => {
    setTextFaces(prev => ({
      ...prev,
      [activeTextFace]: { ...prev[activeTextFace], [key]: value },
    }));
  };

  // Compute which faces have text for the 3D preview
  const activeTextOverlays = Object.fromEntries(
    Object.entries(textFaces).filter(([, v]) => v.text_content?.trim())
  );

  // Fetch available materials on mount
  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${API}/materials`);
        setMaterials(response.data);
        if (response.data.length > 0 && !config.material_id) {
          setConfig(prev => ({ ...prev, material_id: response.data[0].id }));
        }
      } catch (error) {
        console.warn('Materials fetch failed:', error.message);
      }
      setLoadingMaterials(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- One-time mount fetch; config.material_id check prevents overwriting user selection
  }, []);

  // Load saved design or reorder from URL params
  useEffect(() => {
    const reorderId = searchParams.get('reorder');
    if (reorderId && user) {
      // Full reorder: fetch order data and prefill everything
      const loadReorder = async () => {
        try {
          const token = localStorage.getItem('kaha_session');
          const res = await axios.get(`${API}/customer/orders/${reorderId}/reorder-data`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const d = res.data;
          setConfig(prev => ({
            ...prev,
            dimensions: d.dimensions || prev.dimensions,
            material_id: d.material_id || prev.material_id,
            printing: {
              has_printing: d.printing?.has_printing || false,
              coverage_percent: d.printing?.coverage_percent || 0,
              artwork_file_id: d.artwork_file_id || null,
            },
            quantity: d.quantity || prev.quantity,
            billing: d.billing || prev.billing,
            shipping: d.shipping || prev.shipping,
            phone: d.phone || prev.phone,
          }));
          // Set print mode
          if (d.print_mode) setPrintMode(d.print_mode);
          // Set text faces
          if (d.text_customization && typeof d.text_customization === 'object') {
            const faces = ['front', 'back', 'left', 'right'];
            if (faces.some(f => d.text_customization[f])) {
              setTextFaces(prev => ({ ...prev, ...d.text_customization }));
            }
          }
          // Set print side
          if (d.printing?.print_side) setPrintSide(d.printing.print_side);
          // Handle artwork
          if (!d.artwork_available && d.printing?.has_printing && d.print_mode !== 'text') {
            toast.info('Tu archivo anterior no está disponible, por favor súbelo nuevamente');
          }
          // Jump to step 4 (shipping/checkout)
          setCurrentStep(4);
          toast.success(`Reorden de ${d.order_number || 'pedido'} cargada`);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'No se puede reordenar este pedido');
        }
      };
      loadReorder();
      return;
    }

    // Simple URL params (from saved designs)
    const pLength = searchParams.get('length');
    const pWidth = searchParams.get('width');
    const pHeight = searchParams.get('height');
    const pMaterial = searchParams.get('material_id');
    const pQty = searchParams.get('quantity');
    if (pLength || pWidth || pHeight) {
      setConfig(prev => ({
        ...prev,
        dimensions: {
          length: pLength || prev.dimensions.length,
          width: pWidth || prev.dimensions.width,
          height: pHeight || prev.dimensions.height,
        },
        ...(pMaterial ? { material_id: pMaterial } : {}),
        ...(pQty ? { quantity: parseInt(pQty, 10) || 1 } : {}),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-run on URL params or user change; config/state setters are stable or would cause loops
  }, [searchParams, user]);

  // Save design
  const handleSaveDesign = async () => {
    if (!user) {
      toast.error('Inicia sesión para guardar diseños');
      return;
    }
    const { length, width, height } = config.dimensions;
    if (!length || !width || !height) {
      toast.error('Completa las dimensiones antes de guardar');
      return;
    }
    const designName = window.prompt(
      'Nombre para este diseño:',
      `Caja ${mmToCm(length)}x${mmToCm(width)}x${mmToCm(height)} cm`
    );
    if (!designName) return;

    setSavingDesign(true);
    try {
      const selectedMat = materials.find(m => m.id === config.material_id);
      const token = localStorage.getItem('kaha_session');
      await axios.post(`${API}/customer/designs`, {
        name: designName,
        dimensions: { length: parseFloat(length), width: parseFloat(width), height: parseFloat(height) },
        material_id: config.material_id || '',
        material_name: selectedMat?.name || '',
        printing: { ...config.printing, print_mode: printMode },
        text_customization: getTextPayload(),
        quantity: config.quantity,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Diseño guardado');
    } catch (err) {
      toast.error('Error al guardar diseño');
    }
    setSavingDesign(false);
  };

  // Only include faces with actual text content for payloads
  const getTextPayload = () => {
    if (printMode !== 'text') return null;
    const filtered = Object.fromEntries(
      Object.entries(textFaces).filter(([, v]) => v.text_content?.trim())
    );
    return Object.keys(filtered).length > 0 ? filtered : null;
  };

  // Validate dimensions
  const validateDimensions = () => {
    const { length, width, height } = config.dimensions;
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;

    if (l < DIMENSION_LIMITS.length.min ||
        w < DIMENSION_LIMITS.width.min || w > DIMENSION_LIMITS.width.max ||
        h < DIMENSION_LIMITS.height.min) {
      return false;
    }
    return true;
  };

  // Fetch quote whenever config changes
  const fetchQuote = useCallback(async () => {
    const { length, width, height } = config.dimensions;
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;

    // Only fetch if dimensions are valid and material selected
    if (l >= DIMENSION_LIMITS.length.min && 
        w >= DIMENSION_LIMITS.width.min && 
        h >= DIMENSION_LIMITS.height.min &&
        config.quantity > 0 &&
        config.material_id) {
      try {
        const response = await axios.post(`${API}/quote`, {
          dimensions: { length: l, width: w, height: h },
          material_id: config.material_id,
          printing: config.printing,
          quantity: config.quantity
        });
        setQuote(response.data);
      } catch (error) {
        console.warn('Quote error:', error.message);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- API, DIMENSION_LIMITS, setQuote are stable module/state refs
  }, [config.dimensions, config.material_id, config.printing, config.quantity]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQuote();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchQuote]);

  const updateDimension = (key, cmValue) => {
    const mmValue = cmValue === '' ? '' : cmToMm(cmValue);
    setConfig(prev => ({
      ...prev,
      dimensions: { ...prev.dimensions, [key]: mmValue }
    }));
  };

  const updatePrinting = (key, value) => {
    setConfig(prev => ({
      ...prev,
      printing: { ...prev.printing, [key]: value }
    }));
  };

  const handleArtworkUpload = async (e) => {
    const file = e.target?.files?.[0] || e;
    if (!file || !(file instanceof File)) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'cdr'];
    const ext = file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      toast.error('Tipo de archivo no permitido. Use PDF, PNG, JPG o CDR.');
      return;
    }

    setArtworkFile(file);
    setUploadingArtwork(true);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setArtworkPreview(e.target?.result);
      reader.readAsDataURL(file);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/artwork`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      updatePrinting('artwork_file_id', response.data.file_id);
      updatePrinting('has_printing', true);
      updatePrinting('coverage_percent', 50);
      toast.success('Artwork subido correctamente');

      // Validate artwork with AI
      if (file.type.startsWith('image/')) {
        setValidatingArtwork(true);
        try {
          const validationResponse = await axios.post(`${API}/validate-artwork`, {
            file_id: response.data.file_id
          });
          setArtworkValidation(validationResponse.data);
        } catch (err) {
          console.warn('Validation error:', err.message);
        }
        setValidatingArtwork(false);
      }
    } catch (error) {
      toast.error('Error al subir el archivo');
      console.warn('Upload error:', error.message);
    }

    setUploadingArtwork(false);
  };

  const validateCustomerForm = () => {
    const e = {};
    const b = config.billing;
    const s = config.shipping;
    const p = config.phone;

    // Contact info — always required
    if (!b.name_or_company.trim()) e.billing_name = 'Este campo es obligatorio';
    if (!b.email.trim() || !/\S+@\S+\.\S+/.test(b.email)) e.billing_email = 'Ingresa un correo electrónico válido';

    // Phone — always required
    if (!p.country_code) e.phone_code = 'Selecciona un código de país';
    if (!p.number.trim() || p.number.replace(/\s/g, '').length < 7) e.phone_number = 'Ingresa un número de teléfono válido';

    // Shipping — always required
    if (!s.contact_name.trim()) e.shipping_contact = 'Este campo es obligatorio';
    if (!s.state) e.shipping_state = 'Selecciona un estado';
    if (!s.street.trim()) e.shipping_street = 'Este campo es obligatorio';
    if (!s.city.trim()) e.shipping_city = 'Este campo es obligatorio';
    if (!s.postal_code.trim()) e.shipping_postal = 'Ingresa un código postal válido';

    // Invoice fields — only if requested
    if (config.invoice_requested) {
      const razon = (b.razon_social || b.name_or_company).trim();
      if (!razon) e.billing_razon_social = 'Obligatorio para facturación';

      const rfc = (b.rfc || '').trim().toUpperCase();
      const expectedLen = b.billing_type === 'persona_fisica' ? 13 : 12;
      if (!rfc || rfc.length !== expectedLen) {
        e.billing_rfc = `El RFC debe tener ${expectedLen} caracteres`;
      }

      const invEmail = (b.invoice_email || b.email).trim();
      if (!invEmail || !/\S+@\S+\.\S+/.test(invEmail)) e.billing_invoice_email = 'Correo inválido';

      if (!b.fiscal_same_as_shipping) {
        if (!b.state) e.billing_state = 'Selecciona un estado';
        if (!b.street.trim()) e.billing_street = 'Obligatorio';
        if (!b.city.trim()) e.billing_city = 'Obligatorio';
        if (!b.postal_code.trim()) e.billing_postal = 'Código postal obligatorio';
      }
    }

    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmitOrder = async () => {
    if (!validateCustomerForm()) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }
    if (!validateDimensions()) {
      toast.error('Las medidas no son válidas');
      return;
    }

    setIsLoading(true);

    try {
      const { length, width, height } = config.dimensions;

      // Build billing data - if invoice requested, use full fiscal data
      const billingData = { ...config.billing };
      if (config.invoice_requested) {
        billingData.name_or_company = billingData.razon_social || billingData.name_or_company;
        if (billingData.invoice_email) billingData.email = billingData.invoice_email;
        if (billingData.fiscal_same_as_shipping) {
          billingData.state = config.shipping.state;
          billingData.street = config.shipping.street;
          billingData.neighborhood = config.shipping.neighborhood;
          billingData.city = config.shipping.city;
          billingData.postal_code = config.shipping.postal_code;
        }
      }

      const orderData = {
        dimensions: { length: parseFloat(length), width: parseFloat(width), height: parseFloat(height) },
        material_id: config.material_id,
        printing: { ...config.printing, print_side: printSide, print_mode: printMode },
        text_customization: getTextPayload(),
        quantity: config.quantity,
        billing: billingData,
        shipping: config.shipping,
        phone: config.phone,
        invoice_requested: config.invoice_requested,
      };

      const response = await axios.post(`${API}/orders`, orderData);
      toast.success('Orden creada correctamente');

      // Auto-save design on order creation if user is logged in
      if (user) {
        try {
          const selectedMat = materials.find(m => m.id === config.material_id);
          const token = localStorage.getItem('kaha_session');
          await axios.post(`${API}/customer/designs`, {
            name: `Pedido ${response.data.order_number || ''}`.trim(),
            dimensions: { length: parseFloat(length), width: parseFloat(width), height: parseFloat(height) },
            material_id: config.material_id || '',
            material_name: selectedMat?.name || '',
            printing: { ...config.printing, print_mode: printMode },
            text_customization: getTextPayload(),
            quantity: config.quantity,
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (err) { console.warn('Auto-save design failed:', err.message); }
      }

      navigate(`/checkout/${response.data.id}`);
    } catch (error) {
      toast.error('Error al crear la orden');
    }

    setIsLoading(false);
  };

  const nextStep = () => {
    // Validate dimensions when leaving step 1
    if (currentStep === 1) {
      const l = parseFloat(config.dimensions.length) || 0;
      const w = parseFloat(config.dimensions.width) || 0;
      const h = parseFloat(config.dimensions.height) || 0;
      if (w > DIMENSION_LIMITS.width.max) {
        setDimensionError(`El ancho máximo permitido es ${mmToCm(DIMENSION_LIMITS.width.max)} cm. Contacta a hola@kahamx.com para proyectos especiales.`);
        return;
      }
      if (!validateDimensions()) {
        setDimensionError(`Las dimensiones mínimas son ${mmToCm(DIMENSION_LIMITS.length.min)} × ${mmToCm(DIMENSION_LIMITS.width.min)} × ${mmToCm(DIMENSION_LIMITS.height.min)} cm.`);
        return;
      }
      setDimensionError(null);
    }
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setDimensionError(null);
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="step-dimensions">
            <div>
              <h3 className="text-xl font-bold text-primary mb-2">Dimensiones de tu caja</h3>
              <p className="text-muted-foreground text-sm">Ingresa las medidas externas de tu caja en centímetros.</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Ejemplo: 40 × 30 × 20 cm — Tipo FEFCO 0201</p>
            </div>

            {dimensionError && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">{dimensionError}</p>
                </div>
              </div>
            )}

            <div className="grid gap-6">
              <div>
                <Label htmlFor="length" className="text-sm font-semibold uppercase tracking-wide text-primary/60">
                  Largo (L) cm
                </Label>
                <Input
                  id="length"
                  type="number"
                  step="0.1"
                  placeholder="Ej: 50"
                  value={mmToCm(config.dimensions.length)}
                  onChange={(e) => updateDimension('length', e.target.value)}
                  className="mt-2 h-12 text-lg"
                  data-testid="input-length"
                />
              </div>

              <div>
                <Label htmlFor="width" className="text-sm font-semibold uppercase tracking-wide text-primary/60">
                  Ancho (W) cm
                </Label>
                <Input
                  id="width"
                  type="number"
                  step="0.1"
                  placeholder="Ej: 30"
                  value={mmToCm(config.dimensions.width)}
                  onChange={(e) => updateDimension('width', e.target.value)}
                  className="mt-2 h-12 text-lg"
                  data-testid="input-width"
                />
              </div>

              <div>
                <Label htmlFor="height" className="text-sm font-semibold uppercase tracking-wide text-primary/60">
                  Alto (H) cm
                </Label>
                <Input
                  id="height"
                  type="number"
                  step="0.1"
                  placeholder="Ej: 20"
                  value={mmToCm(config.dimensions.height)}
                  onChange={(e) => updateDimension('height', e.target.value)}
                  className="mt-2 h-12 text-lg"
                  data-testid="input-height"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="step-material">
            <div>
              <h3 className="text-xl font-bold text-primary mb-2">Material</h3>
              <p className="text-muted-foreground text-sm">Selecciona el tipo de cartón corrugado</p>
            </div>

            {loadingMaterials ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
                <span className="ml-2 text-muted-foreground">Cargando materiales...</span>
              </div>
            ) : (
              <div className="grid gap-3">
                {materials.map((mat) => {
                  const isSelected = config.material_id === mat.id;
                  const meta = getMaterialMeta(mat.name);
                  return (
                    <button
                      key={mat.id}
                      type="button"
                      onClick={() => setConfig(prev => ({ ...prev, material_id: mat.id }))}
                      className={`w-full text-left p-4 border-2 rounded-xl transition-all ${
                        isSelected
                          ? 'border-accent bg-accent/5 shadow-md'
                          : 'border-border hover:border-accent/40 hover:bg-muted/30'
                      }`}
                      data-testid={`material-card-${mat.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-base">{mat.name}</span>
                            {isSelected && <Check className="w-5 h-5 text-accent" />}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {meta.description || mat.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <ResistanceIndicator level={meta.resistance} size="xs" />
                            <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted">
                              {mat.type === 'sencillo' ? 'Pared sencilla' : 'Doble pared'}
                            </span>
                            <span className="text-xs text-muted-foreground">{mat.thickness_mm} mm</span>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-lg font-bold text-accent">${mat.cost_per_m2.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">MXN/m²</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-muted-foreground/60 text-center pt-2">
              ¿No estás seguro? Te ayudamos a elegir el material ideal.
            </p>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="step-printing">
            <div>
              <h3 className="text-xl font-bold text-primary mb-2">Impresión</h3>
              <p className="text-muted-foreground text-sm">Sube tu diseño en PDF, PNG o CDR. Tu diseño se ajustara automaticamente al area imprimible de la caja.</p>
            </div>

            <div className="space-y-4">
              <Label className="text-sm font-semibold uppercase tracking-wide text-primary/60">
                ¿Deseas imprimir tu diseño?
              </Label>
              <RadioGroup
                value={config.printing.has_printing ? 'yes' : 'no'}
                onValueChange={(value) => {
                  updatePrinting('has_printing', value === 'yes');
                  if (value === 'yes') {
                    updatePrinting('coverage_percent', 50); // Default 50% coverage
                  } else {
                    updatePrinting('coverage_percent', 0);
                    updatePrinting('artwork_file_id', null);
                    setArtworkFile(null);
                    setArtworkPreview(null);
                    setArtworkValidation(null);
                    setPrintSide('front');
                  }
                }}
                className="grid grid-cols-2 gap-3"
              >
                <div className={`flex items-center justify-center p-4 border rounded-lg cursor-pointer transition-all ${!config.printing.has_printing ? 'border-accent bg-accent/5' : ''}`}>
                  <RadioGroupItem value="no" id="no_print" className="sr-only" />
                  <Label htmlFor="no_print" className="cursor-pointer text-center">
                    <Package className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <span className="font-semibold">Sin impresión</span>
                  </Label>
                </div>
                <div className={`flex items-center justify-center p-4 border rounded-lg cursor-pointer transition-all ${config.printing.has_printing ? 'border-accent bg-accent/5' : ''}`}>
                  <RadioGroupItem value="yes" id="yes_print" className="sr-only" />
                  <Label htmlFor="yes_print" className="cursor-pointer text-center">
                    <Printer className="w-8 h-8 mx-auto mb-2 text-accent" />
                    <span className="font-semibold">Con impresión</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {config.printing.has_printing && (
              <>
                {/* Mode selector */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold uppercase tracking-wide text-primary/60">
                    Personaliza tu impresión
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setPrintMode('file')}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        printMode === 'file' ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/40'
                      }`} data-testid="mode-file">
                      <Upload className="w-7 h-7 mx-auto mb-1.5 text-accent" />
                      <span className="text-sm font-semibold block">Subir archivo</span>
                      <span className="text-xs text-muted-foreground">PDF, PNG, CDR</span>
                    </button>
                    <button type="button" onClick={() => setPrintMode('text')}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        printMode === 'text' ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/40'
                      }`} data-testid="mode-text">
                      <Type className="w-7 h-7 mx-auto mb-1.5 text-accent" />
                      <span className="text-sm font-semibold block">Agregar texto</span>
                      <span className="text-xs text-muted-foreground">Texto personalizado</span>
                    </button>
                  </div>
                </div>

                {/* FILE MODE */}
                {printMode === 'file' && (
                  <div className="space-y-4">
                    <Label className="text-sm font-semibold uppercase tracking-wide text-primary/60">
                      Sube tu diseño
                    </Label>
                    <div
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200 ${
                        isDragOver
                          ? 'border-accent bg-accent/5 scale-[1.01]'
                          : artworkPreview
                          ? 'border-accent/40 bg-accent/5'
                          : 'border-border hover:border-accent/60 hover:bg-muted/30'
                      }`}
                      onClick={() => !uploadingArtwork && fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
                      onDrop={(e) => {
                        e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
                        const droppedFile = e.dataTransfer.files?.[0];
                        if (droppedFile) handleArtworkUpload(droppedFile);
                      }}
                      data-testid="dropzone-artwork"
                    >
                      {artworkPreview ? (
                        <div className="space-y-3">
                          <img src={artworkPreview} alt="Preview" className="max-h-32 mx-auto rounded" />
                          <p className="text-sm font-medium text-foreground truncate px-4">{artworkFile?.name}</p>
                          <p className="text-xs text-muted-foreground">Haz clic para cambiar archivo</p>
                          {validatingArtwork && (
                            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                              <Loader2 className="w-4 h-4 animate-spin" /> Validando con IA...
                            </div>
                          )}
                        </div>
                      ) : uploadingArtwork ? (
                        <div className="flex flex-col items-center justify-center gap-2 py-4">
                          <Loader2 className="w-8 h-8 animate-spin text-accent" />
                          <p className="text-sm text-muted-foreground">Subiendo archivo...</p>
                        </div>
                      ) : isDragOver ? (
                        <div className="py-4">
                          <Upload className="w-12 h-12 mx-auto text-accent mb-2 animate-bounce" />
                          <p className="text-sm font-medium text-accent">Suelta tu archivo aquí</p>
                        </div>
                      ) : (
                        <div className="py-2">
                          <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                          <p className="text-sm font-medium text-foreground/80">Haz clic o arrastra tu archivo aquí</p>
                          <p className="text-xs text-muted-foreground mt-1.5">Formatos permitidos: PDF, PNG, JPG o CDR</p>
                        </div>
                      )}
                      <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.cdr"
                        onChange={handleArtworkUpload} className="hidden" data-testid="input-artwork" />
                    </div>
                  </div>
                )}

                {/* TEXT MODE */}
                {printMode === 'text' && (
                  <div className="space-y-4 animate-fadeIn">
                    {/* Face tabs */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide text-primary/60">Cara de la caja</Label>
                      <div className="flex gap-1.5">
                        {FACE_TABS.map(f => {
                          const hasTxt = textFaces[f.value]?.text_content?.trim();
                          return (
                            <button key={f.value} type="button"
                              onClick={() => setActiveTextFace(f.value)}
                              className={`flex-1 py-2 text-xs rounded-lg border transition-all relative ${
                                activeTextFace === f.value
                                  ? 'border-accent bg-accent/10 text-accent font-semibold'
                                  : 'border-border hover:border-accent/40'
                              }`}
                              data-testid={`text-face-${f.value}`}
                            >
                              {f.label}
                              {hasTxt && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-accent rounded-full" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground">Puedes escribir texto diferente en cada cara</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide text-primary/60">Texto</Label>
                      <textarea
                        value={textCustomization.text_content}
                        onChange={(e) => updateText('text_content', e.target.value.slice(0, TEXT_MAX_CHARS))}
                        placeholder={`Texto para la cara: ${FACE_TABS.find(f => f.value === activeTextFace)?.label || activeTextFace}`}
                        className="w-full min-h-[80px] p-3 rounded-lg border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent/40"
                        maxLength={TEXT_MAX_CHARS}
                        data-testid="input-text-content"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>La vista previa se actualizara automaticamente.</span>
                        <span className={textCustomization.text_content.length >= TEXT_MAX_CHARS ? 'text-red-500 font-semibold' : ''}>
                          {textCustomization.text_content.length}/{TEXT_MAX_CHARS}
                        </span>
                      </div>
                      {textCustomization.text_content.length >= TEXT_MAX_CHARS && (
                        <p className="text-xs text-red-500">Tu texto excede el limite permitido</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Color del texto</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={textCustomization.text_color}
                            onChange={(e) => {
                              const hex = e.target.value.toLowerCase();
                              if (hex === '#ffffff' || hex === '#fff') return;
                              updateText('text_color', hex);
                            }}
                            className="w-9 h-9 rounded border border-border cursor-pointer"
                            data-testid="input-text-color" />
                          <span className="text-xs font-mono text-muted-foreground">{textCustomization.text_color}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground">No se puede imprimir en blanco (#FFFFFF)</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Tipografia</Label>
                        <select value={textCustomization.font_family}
                          onChange={(e) => updateText('font_family', e.target.value)}
                          className="w-full h-9 px-2 rounded-lg border border-border bg-background text-sm"
                          data-testid="select-font">
                          {FONT_OPTIONS.map(f => (
                            <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Tamaño</Label>
                        <div className="flex gap-1.5">
                          {SIZE_OPTIONS.map(s => (
                            <button key={s.value} type="button"
                              onClick={() => updateText('font_size', s.value)}
                              className={`flex-1 py-1.5 text-xs rounded-lg border transition-all ${
                                textCustomization.font_size === s.value
                                  ? 'border-accent bg-accent/10 text-accent font-semibold'
                                  : 'border-border hover:border-accent/40'
                              }`} data-testid={`size-${s.value}`}>
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Alineacion</Label>
                        <div className="flex gap-1.5">
                          {ALIGN_OPTIONS.map(a => (
                            <button key={a.value} type="button"
                              onClick={() => updateText('text_alignment', a.value)}
                              className={`flex-1 py-1.5 text-xs rounded-lg border transition-all ${
                                textCustomization.text_alignment === a.value
                                  ? 'border-accent bg-accent/10 text-accent font-semibold'
                                  : 'border-border hover:border-accent/40'
                              }`} data-testid={`align-${a.value}`}>
                              {a.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {artworkValidation && (
                  <div className={`p-4 rounded-lg ${artworkValidation.is_valid ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                    <div className="flex items-start gap-3">
                      {artworkValidation.is_valid ? (
                        <Check className="w-5 h-5 text-green-600 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      )}
                      <div>
                        <p className="font-semibold text-sm">
                          {artworkValidation.is_valid ? 'Artwork validado' : 'Sugerencias'}
                        </p>
                        {artworkValidation.issues?.length > 0 && (
                          <ul className="text-sm text-muted-foreground mt-1 list-disc list-inside">
                            {artworkValidation.issues.map((issue, i) => (
                              <li key={`issue-${i}-${issue.slice(0,10)}`}>{issue}</li>
                            ))}
                          </ul>
                        )}
                        {artworkValidation.suggestions?.length > 0 && (
                          <ul className="text-sm text-muted-foreground mt-1">
                            {artworkValidation.suggestions.map((suggestion, i) => (
                              <li key={`sug-${i}-${suggestion.slice(0,10)}`}>• {suggestion}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <Label className="text-sm font-semibold uppercase tracking-wide text-primary/60">
                    Cobertura de impresión: {config.printing.coverage_percent}%
                  </Label>
                  <Slider
                    value={[config.printing.coverage_percent]}
                    onValueChange={(value) => updatePrinting('coverage_percent', value[0])}
                    min={10}
                    max={100}
                    step={5}
                    className="w-full"
                    data-testid="slider-coverage"
                  />
                  <p className="text-xs text-muted-foreground">
                    Mayor cobertura = más uso de tinta = mayor costo
                  </p>
                </div>

                <div className="space-y-4">
                  <Label className="text-sm font-semibold uppercase tracking-wide text-primary/60">
                    Lado de impresión
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'front', label: 'Frente' },
                      { value: 'back', label: 'Atrás' },
                      { value: 'left', label: 'Izquierda' },
                      { value: 'right', label: 'Derecha' },
                      { value: 'top', label: 'Arriba' },
                      { value: 'all', label: 'Todos' },
                    ].map((side) => (
                      <button
                        key={side.value}
                        type="button"
                        onClick={() => setPrintSide(side.value)}
                        className={`p-3 border rounded-lg text-sm font-medium transition-all ${
                          printSide === side.value
                            ? 'border-accent bg-accent/10 text-accent'
                            : 'border-border hover:border-muted-foreground'
                        }`}
                        data-testid={`side-${side.value}`}
                      >
                        {side.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Selecciona en qué lado(s) de la caja aparecerá tu diseño
                  </p>
                </div>
              </>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="step-quantity">
            <div>
              <h3 className="text-xl font-bold text-primary mb-2">Cantidad y Datos del Cliente</h3>
              <p className="text-muted-foreground text-sm">Sin mínimo de pedido. Entrega en 5 días hábiles.</p>
              <p className="text-xs text-muted-foreground/60 mt-1">El costo de envío se calcula antes de finalizar tu compra.</p>
            </div>

            <div className="space-y-4">
              <Label className="text-sm font-semibold uppercase tracking-wide text-primary/60">
                Cantidad de cajas
              </Label>
              <Input
                type="number"
                min={1}
                value={config.quantity === 0 ? '' : config.quantity}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setConfig(prev => ({ ...prev, quantity: 0 }));
                  } else {
                    setConfig(prev => ({ ...prev, quantity: Math.max(1, parseInt(raw) || 0) }));
                  }
                }}
                className="h-12 text-lg"
                placeholder="0"
                data-testid="input-quantity"
              />
            </div>

            <div className="border-t pt-6">
              <CustomerForm
                billing={config.billing}
                shipping={config.shipping}
                phone={config.phone}
                invoiceRequested={config.invoice_requested}
                onBillingChange={(b) => setConfig(prev => ({ ...prev, billing: b }))}
                onShippingChange={(s) => setConfig(prev => ({ ...prev, shipping: s }))}
                onPhoneChange={(p) => setConfig(prev => ({ ...prev, phone: p }))}
                onInvoiceRequestedChange={(v) => setConfig(prev => ({ ...prev, invoice_requested: v }))}
                errors={formErrors}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="logo-link">
            <Package className="w-8 h-8 text-accent" />
            <span className="text-xl font-bold text-primary">KAHA</span>
          </a>
          <span className="text-sm text-muted-foreground">Configurador de Cajas</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className="flex flex-col items-center gap-2"
                  data-testid={`step-nav-${step.id}`}
                >
                  <div className={`step-indicator ${
                    currentStep === step.id ? 'active' : 
                    currentStep > step.id ? 'completed' : 'pending'
                  }`}>
                    {currentStep > step.id ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${
                    currentStep === step.id ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    {step.name}
                  </span>
                </button>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-accent' : 'bg-border'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <Card className="shadow-soft">
            <CardContent className="p-6 sm:p-8">
              {renderStep()}

              {/* Navigation */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="gap-2"
                  data-testid="btn-prev"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Anterior
                </Button>

                <div className="flex items-center gap-2">
                  {user && config.dimensions.length && config.dimensions.width && config.dimensions.height && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSaveDesign}
                      disabled={savingDesign}
                      className="gap-1.5 border-accent/40 text-accent hover:bg-accent/5"
                      data-testid="btn-save-design"
                    >
                      {savingDesign ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bookmark className="w-3.5 h-3.5" />}
                      <span className="hidden sm:inline">Guardar diseño</span>
                    </Button>
                  )}
                
                  {currentStep < 4 ? (
                    <Button onClick={nextStep} className="gap-2 bg-primary text-white" data-testid="btn-next">
                      Siguiente
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleSubmitOrder} 
                      disabled={isLoading || !config.billing.name_or_company || !config.billing.email || !config.shipping.contact_name}
                      className="gap-2 bg-accent hover:bg-accent/90 text-white"
                      data-testid="btn-submit-order"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          Continuar al pago
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview & Quote */}
          <div className="space-y-6">
            {/* 3D Preview */}
            <Card className="shadow-soft overflow-hidden">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-accent" />
                  Vista previa 3D
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 bg-gradient-to-br from-muted/20 to-muted/40">
                <Box3DInteractive
                  length={config.dimensions.length}
                  width={config.dimensions.width}
                  height={config.dimensions.height}
                  artworkUrl={printMode === 'file' ? artworkPreview : null}
                  coveragePercent={config.printing.coverage_percent}
                  printSide={printSide}
                  isWhiteMaterial={(() => {
                    const mat = materials.find(m => m.id === config.material_id);
                    return mat ? mat.name.toLowerCase().includes('blanco') : false;
                  })()}
                  textOverlay={printMode === 'text' && config.printing.has_printing ? textCustomization : null}
                  textOverlays={printMode === 'text' && config.printing.has_printing ? activeTextOverlays : null}
                />
              </CardContent>
            </Card>

            {/* Quote Summary */}
            <Card className="shadow-soft" data-testid="quote-summary">
              <CardHeader className="border-b bg-primary text-white">
                <CardTitle className="text-lg">Resumen de Cotización</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {quote ? (
                  <>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Dimensiones</p>
                        <p className="font-semibold">{mmToCm(config.dimensions.length) || '-'} × {mmToCm(config.dimensions.width) || '-'} × {mmToCm(config.dimensions.height) || '-'} cm</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Material</p>
                        <p className="font-semibold">{quote.material_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Cantidad</p>
                        <p className="font-semibold">{config.quantity.toLocaleString()} {config.quantity === 1 ? 'caja' : 'cajas'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Impresión</p>
                        <p className="font-semibold">{config.printing.has_printing ? `Sí (${config.printing.coverage_percent}%)` : 'No'}</p>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Precio por unidad (sin IVA)</span>
                        <span className="text-lg font-bold">${quote.unit_price.toFixed(2)} MXN</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-muted-foreground mt-1">
                        <span>Precio por unidad (con IVA {quote.iva_pct}%)</span>
                        <span>${quote.unit_price_with_tax?.toFixed(2) || (quote.unit_price * 1.16).toFixed(2)} MXN</span>
                      </div>
                      <div className="flex justify-between items-center mt-4 pt-4 border-t">
                        <span className="font-semibold text-lg">Total (sin IVA)</span>
                        <span className="text-xl font-bold">
                          ${quote.total_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="font-semibold text-lg">Total (con IVA)</span>
                        <span className="text-2xl font-bold text-accent" data-testid="total-price">
                          ${(quote.total_price_with_tax || quote.total_price * 1.16).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                        </span>
                      </div>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4 text-sm">
                      <p className="text-muted-foreground text-center">
                        Entrega estimada: 5 días hábiles
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-4 opacity-40" />
                    <p>Ingresa las dimensiones para ver la cotización</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Configurator;
;
